import type { CmpConfigPayload, ConsentState, GvlPurpose, GvlVendor } from "./types";
import { NO_LI_PURPOSE_IDS } from "./types";
import { computeDisclosure, type Disclosure } from "./disclosure";

// Device-storage disclosures are not part of config.json; they are loaded lazily
// from /c/dsd/<vendorListVersion>/<vendorId>.json when a vendor's details open.
let dsdBase = "";
export function setDisclosureBase(apiBase: string) { dsdBase = String(apiBase || "").replace(/\/$/, ""); }
const dsdCache = new Map<number, unknown>();
const dsdPending = new Set<number>();

/** English fallbacks; localized texts come from config.banner.texts. */
const FALLBACK: Record<string, string> = {
  intro: "We and our %{vendorCount} partners store and/or access information on a device, such as cookies, and process personal data, such as unique identifiers and standard information sent by a device.",
  purposesIntro: "We and our partners process data for the following purposes:",
  specialFeaturesIntro: "With your acceptance, we and our partners also use the following special features:",
  liNotice: "%{liVendorCount} partners process your data without your consent on the basis of a legitimate interest. You have the right to object to this processing.",
  liLink: "View legitimate interest and object",
  consequences: "",
  scope: "Your choices apply to this website only.",
  withdraw: "You can withdraw your consent or change your choices at any time via “%{revisit}”.",
  stackIncludes: "Included purposes",
  choicesNotice: "Your choices regarding partners are limited to purposes and special features. You cannot object to processing for special purposes. Special features may be used for the special purpose “Ensure security, prevent and detect fraud, and fix errors” regardless of your choice.",
  specialPurposesNotice: "Partners rely on a legitimate interest for these purposes; objecting is not possible via these settings.",
  liTabIntro: "Some partners process your personal data without consent on the basis of a legitimate interest. You can object per purpose and per partner.",
  vendorCountConsent: "%{count} partners ask for consent",
  vendorCountLi: "%{count} partners rely on legitimate interest",
  vendorCountUsing: "%{count} partners",
  showVendors: "Show partners",
  nonIabNotice: "These services do not participate in the IAB Europe Transparency & Consent Framework.",
  legitimateInterestTab: "Legitimate interest",
  legIntAllowed: "Legitimate interest (untick to object)",
  withdrawAll: "Withdraw all consent",
  cmpStorage: "Your choices are stored in cookies on your device for up to %{days} days.",
  vendorLiNote: "Object in the “Legitimate interest” section",
  deviceStorageDisclosureLink: "Device storage disclosure (JSON)",
  cookieRefreshNote: "The duration may be renewed from your last interaction with this website.",
  iabVendorsCount: "%{count} partners registered with the IAB TCF"
};

const CMP_COOKIE_DAYS = 365;

export type UiCallbacks = {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onOpenSettings: () => void;
  onCloseSettings: () => void;
  onSave: (state: ConsentState) => void;
  onDoNotSell?: () => void;
};

export class BannerUI {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private layer: "first" | "second" = "first";
  private state: ConsentState;
  private config: CmpConfigPayload;
  private callbacks: UiCallbacks;
  private revisit?: HTMLButtonElement;
  private activeTab = "purposes";
  private vendorDetailId: number | null = null;
  private disclosure: Disclosure;
  /** true when re-surfaced with an existing decision (offer "withdraw all"). */
  private hasPriorDecision = false;
  private focusTab: string | null = null;

  constructor(config: CmpConfigPayload, initial: ConsentState, callbacks: UiCallbacks) {
    this.config = config;
    this.state = structuredClone(initial);
    this.callbacks = callbacks;
    this.disclosure = computeDisclosure(config);
    this.host = document.createElement("div");
    this.host.id = "cmp-root";
    document.body.appendChild(this.host);
    this.shadow = this.host.attachShadow({ mode: "open" });
  }

  setPriorDecision(v: boolean) { this.hasPriorDecision = v; }

  showFirstLayer() { this.layer = "first"; this.vendorDetailId = null; this.render(); this.emitShown(); }
  showSecondLayer() { this.layer = "second"; this.render(); this.emitShown(); }
  destroy() { this.host.remove(); this.hideRevisit(); }

  showRevisit() {
    const re = this.config.banner.ui.revisitEnabled; if (re === false || re === "false") return;
    if (this.revisit) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "cmp-revisit";
    btn.textContent = this.config.banner.texts.revisit || "Privacy settings";
    const pos = this.config.banner.ui.revisitPosition || "bottom-left";
    const corner = pos.includes("right") ? "right:16px;" : "left:16px;";
    // --adcmp-console-*: space reserved by the debug console (cmp-console.js), 0 otherwise.
    const vert = pos.includes("top") ? "top:calc(16px + var(--adcmp-console-top,0px));" : "bottom:calc(16px + var(--adcmp-console-bottom,0px));";
    btn.style.cssText = `position:fixed;${corner}${vert}z-index:2147483001;border:0;border-radius:999px;padding:10px 14px;background:#0f766e;color:#fff;font:12px system-ui;cursor:pointer;box-shadow:0 8px 20px rgba(0,0,0,.2)`;
    btn.addEventListener("click", () => this.callbacks.onOpenSettings());
    document.body.appendChild(btn);
    this.revisit = btn;
  }

  hideRevisit() { this.revisit?.remove(); this.revisit = undefined; }

  private emitShown() { this.host.style.display = "block"; }
  private t(): Record<string, string> { return { ...FALLBACK, ...(this.config.banner.texts || {}) }; }
  private ui() { return this.config.banner.ui || {}; }

  private render() {
    const t = this.t();
    const ui = this.ui();
    const primary = ui.primaryColor || "#0f766e";
    const bg = ui.backgroundColor || "#ffffff";
    const text = ui.textColor || "#0f172a";
    const vendorCount = (this.config.gvl.vendors?.length || 0) + (this.config.customVendors?.length || 0);
    const isUs = this.config.regime === "gpp_us";

    this.shadow.innerHTML = `
      <style>
        :host{all:initial}
        *{box-sizing:border-box;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif}
        .overlay{position:fixed;inset:var(--adcmp-console-top,0px) 0 var(--adcmp-console-bottom,0px) 0;background:rgba(15,23,42,.45);z-index:2147483000;display:flex;align-items:flex-end;justify-content:center;padding:16px}
        .panel{background:${bg};color:${text};max-width:720px;width:100%;border-radius:16px;box-shadow:0 20px 50px rgba(0,0,0,.25);padding:20px;max-height:min(92vh,800px);overflow:auto;border:1px solid ${primary}33}
        h2{margin:0 0 8px;font-size:1.15rem}
        h3{margin:0;font-size:.95rem}
        p{margin:0 0 10px;line-height:1.45;font-size:.92rem}
        .meta,.notice{font-size:.8rem;color:#64748b;margin-bottom:8px}
        .notices{display:grid;gap:4px;margin:8px 0 12px}
        .actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px}
        button{border:0;border-radius:10px;padding:10px 14px;font-weight:600;cursor:pointer;font-size:.9rem}
        .primary,.secondary{min-width:140px}
        .primary{background:${primary};color:#fff}
        .secondary{background:#e2e8f0;color:#0f172a}
        .ghost{background:transparent;color:${primary};text-decoration:underline;padding-left:4px;padding-right:4px}
        .chip-list{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0 12px}
        .chip{background:#f1f5f9;border-radius:999px;padding:4px 10px;font-size:.75rem}
        .row{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;padding:10px 0;border-top:1px solid #e2e8f0}
        .row p{margin:4px 0 0;font-size:.8rem;color:#64748b}
        .illus{margin:4px 0 0;padding-left:16px;font-size:.75rem;color:#64748b}
        .toggles{display:flex;flex-direction:column;gap:4px;min-width:150px}
        label.toggle{display:flex;align-items:center;gap:6px;font-size:.75rem}
        .tabs{display:flex;gap:8px;margin:12px 0;flex-wrap:wrap}
        .tab{background:#f1f5f9}
        .tab[aria-selected="true"]{background:${primary};color:#fff}
        .detail{background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:12px;margin:8px 0;font-size:.8rem}
        .detail a{color:${primary}}
        .detail dl{display:grid;grid-template-columns:140px 1fr;gap:4px 8px;margin:8px 0}
        .detail dt{color:#64748b}
        :focus-visible{outline:2px solid ${primary};outline-offset:2px}
        ul.plist{margin:4px 0 12px;padding-left:18px;font-size:.88rem;line-height:1.45}
        ul.plist li{margin:2px 0}
        ul.plist details summary{cursor:pointer}
        ul.plist ul{margin:4px 0;padding-left:16px;font-size:.8rem;color:#475569}
        .lead-sm{font-size:.88rem;font-weight:600;margin:10px 0 2px}
        details.vlist{font-size:.75rem;color:#475569;margin-top:4px}
        details.vlist summary{cursor:pointer;color:${primary}}
        .std{background:#f8fafc;border-left:3px solid ${primary};padding:8px 10px;font-size:.8rem;color:#334155;margin:8px 0}
        .section-title{margin:14px 0 4px}
        .li-link{color:${primary};font-weight:600}
        .branding{margin-top:14px;padding-top:10px;border-top:1px solid #e2e8f0;text-align:center}
        .branding a{font-size:.7rem;color:#64748b;text-decoration:none}
        .branding a:hover{color:#047857;text-decoration:underline}
      </style>
      <div class="overlay">
        <div class="panel" role="dialog" aria-modal="true" aria-labelledby="cmp-title" tabindex="-1">
          ${this.layer === "first" ? this.first(t, vendorCount, isUs) : this.second(t)}
        </div>
      </div>`;
    this.bind();
    const focusTarget = this.focusTab
      ? this.shadow.querySelector(`#cmp-tab-${this.focusTab}`) as HTMLElement | null
      : this.shadow.querySelector(".panel") as HTMLElement | null;
    this.focusTab = null;
    focusTarget?.focus();
  }

  private first(t: Record<string, string>, vendorCount: number, isUs: boolean) {
    if (isUs) {
      return `<h2 id="cmp-title">${esc(t.usTitle || "Your Privacy Choices")}</h2>
        <p>${esc(t.usDescription || "We and our partners process data for advertising and analytics. You can opt out of the sale or sharing of personal information.")}</p>
        <div class="actions">
          <button type="button" class="primary" data-a="accept">${esc(t.acceptAll || "Accept")}</button>
          <button type="button" class="secondary" data-a="dns">${esc(t.doNotSell || "Do Not Sell or Share My Personal Information")}</button>
          <button type="button" class="ghost" data-a="settings">${esc(t.customize || "Settings")}</button>
        </div>`;
    }

    const d = this.disclosure;
    const purposes = this.config.gvl.purposes || [];
    const pname = (id: number) => purposes.find((p) => p.id === id)?.name || String(id);
    // Stacks (server-selected, non-overlapping, only purposes actually used) replace
    // their purposes; every other purpose is named individually (Appendix B C(b)(IV)).
    const stacks = (this.config.gvl.stacks || []).filter((s) => (s.purposes || []).length > 0 &&
      (s.purposes || []).every((id) => purposes.some((p) => p.id === id)));
    const stacked = new Set(stacks.flatMap((s) => s.purposes || []));
    const items = [
      ...purposes.filter((p) => !stacked.has(p.id)).map((p) => `<li>${esc(p.name)}</li>`),
      ...stacks.map((s) => `<li><details><summary>${esc(s.name)}</summary><ul>${(s.purposes || []).map((id) => `<li>${esc(pname(id))}</li>`).join("")}</ul></details></li>`)
    ];
    // Special features are always named individually (Appendix B C(b)(V)).
    const sfs = (this.config.gvl.specialFeatures || []).filter((f) => d.specialFeatureVendors.has(f.id));

    const revisitLabel = t.revisit || "Privacy settings";
    const liCount = d.liVendorIds.length;
    const liSentence = liCount > 0
      ? `<p>${esc(fill(t.liNotice, { liVendorCount: liCount }))} <a href="#" class="li-link" data-a="li-link">${esc(t.liLink)}</a></p>`
      : "";
    const closing = [t.consequences, t.scope, fill(t.withdraw, { revisit: revisitLabel })].filter(Boolean).map(esc).join(" ");

    return `<h2 id="cmp-title">${esc(t.title || "Privacy")}</h2>
      <p>${esc(fill(t.intro, { vendorCount }))}</p>
      ${items.length ? `<p class="lead-sm">${esc(t.purposesIntro)}</p><ul class="plist" aria-label="${esc(t.purposesTitle || "Purposes")}">${items.join("")}</ul>` : ""}
      ${sfs.length ? `<p class="lead-sm">${esc(t.specialFeaturesIntro)}</p><ul class="plist" aria-label="${esc(t.specialFeaturesTitle || "Special features")}">${sfs.map((f) => `<li>${esc(f.name)}</li>`).join("")}</ul>` : ""}
      ${liSentence}
      <p>${closing}</p>
      <div class="notice"><a href="#" data-a="vendors-link">${esc(t.vendorListLink || "View partner list")} (${vendorCount})</a> · IAB TCF</div>
      <div class="actions">
        <button type="button" class="primary" data-a="accept">${esc(t.acceptAll || "Accept all")}</button>
        <button type="button" class="secondary" data-a="reject">${esc(t.rejectAll || "Reject all")}</button>
        <button type="button" class="ghost" data-a="settings">${esc(t.customize || "Manage choices")}</button>
      </div>`;
  }

  private second(t: Record<string, string>): string {
    if (this.vendorDetailId != null) return this.vendorDetail(t, this.vendorDetailId);
    const d = this.disclosure;
    const gvl = this.config.gvl;
    const vname = (id: number) => (gvl.vendors || []).find((v) => v.id === id)?.name || `#${id}`;
    const pname = (id: number) => (gvl.purposes || []).find((p) => p.id === id)?.name || String(id);
    const hasLi = d.liVendorIds.length > 0;

    const purposes = (gvl.purposes || []).map((p) => this.purposeRow(t, p)).join("");
    const specialPurposes = (gvl.specialPurposes || []).filter((p) => d.specialPurposeVendors.has(p.id)).map((p) => `
      <div class="row"><div><h3>${esc(p.name)}</h3><p>${esc(p.description || "")}</p>${illustrations(t, p)}
      ${vendorList(t, t.vendorCountUsing, d.specialPurposeVendors.get(p.id) || [], vname)}</div></div>`).join("");
    const specialFeatures = (gvl.specialFeatures || []).filter((f) => d.specialFeatureVendors.has(f.id)).map((f) => `
      <div class="row"><div><h3>${esc(f.name)}</h3><p>${esc(f.description || "")}</p>${illustrations(t, f)}
      ${vendorList(t, t.vendorCountUsing, d.specialFeatureVendors.get(f.id) || [], vname)}</div>
      <div class="toggles">
        <label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.optIn || t.consent || "Opt-in"}: ${f.name}`)}" data-sf="${f.id}" ${this.state.specialFeatureOptins[f.id] ? "checked" : ""}/> ${esc(t.optIn || t.consent || "Opt-in")}</label>
      </div></div>`).join("");
    // TCF v2.4 / Policy 5.0.b Appendix B(B)(f): standard feature explanation, no controls.
    const featureStd = gvl.standardTexts?.features || "";
    const features = (gvl.features || []).filter((f) => d.featureVendors.has(f.id)).map((f) => `
      <div class="row"><div><h3>${esc(f.name)}</h3><p>${esc(f.description || "")}</p>${illustrations(t, f)}
      ${vendorList(t, t.vendorCountUsing, d.featureVendors.get(f.id) || [], vname)}</div></div>`).join("");

    const liPurposes = (gvl.purposes || []).filter((p) => d.liPurposeVendors.has(p.id)).map((p) => `
      <div class="row"><div><h3>${esc(p.name)}</h3><p>${esc(p.description || "")}</p>${illustrations(t, p)}
      ${vendorList(t, t.vendorCountLi, d.liPurposeVendors.get(p.id) || [], vname)}</div>
      <div class="toggles">
        <label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.legitimateInterest || "Legitimate interest"}: ${p.name}`)}" data-pli="${p.id}" ${this.state.purposeLegitimateInterests[p.id] ? "checked" : ""}/> ${esc(t.legIntAllowed)}</label>
      </div></div>`).join("");
    const liVendors = d.liVendorIds.map((id) => {
      const v = (gvl.vendors || []).find((x) => x.id === id);
      const b = d.vendors.get(id)!;
      return `<div class="row"><div><h3><a href="#" data-vd="${id}">${esc(vname(id))}</a> <small>#${id}</small></h3>
        <p>${esc(t.legitimateInterest || "LI")}: ${esc(b.legInt.map(pname).join(", "))}</p>
        ${v?.legIntClaimUrl ? `<p><a href="${esc(v.legIntClaimUrl)}" target="_blank" rel="noopener">${esc(t.legIntClaim || "Legitimate interest information")}</a></p>` : ""}</div>
        <div class="toggles"><label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.legitimateInterest || "Legitimate interest"}: ${vname(id)}`)}" data-vli="${id}" ${this.state.vendorLegitimateInterests[id] ? "checked" : ""}/> ${esc(t.legIntAllowed)}</label></div></div>`;
    }).join("");

    const vendors = (gvl.vendors || []).map((v) => {
      const b = d.vendors.get(v.id)!;
      const liText = b.legInt.length ? ` · ${esc(t.legitimateInterest || "LI")}: ${esc(b.legInt.map(pname).join(", "))}` : "";
      return `<div class="row"><div><h3><a href="#" data-vd="${v.id}">${esc(v.name)}</a> <small>#${v.id}</small></h3>
      <p>${esc(t.consent || "Consent")}: ${esc(b.consent.map(pname).join(", ") || "–")}${liText}</p>
      ${v.policyUrl ? `<p><a href="${esc(v.policyUrl)}" target="_blank" rel="noopener">${esc(t.privacyPolicy || "Privacy policy")}</a></p>` : ""}</div>
      <div class="toggles">
        ${b.consent.length ? `<label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.consent || "Consent"}: ${v.name}`)}" data-vc="${v.id}" ${this.state.vendorConsents[v.id] ? "checked" : ""}/> ${esc(t.consent || "Consent")}</label>` : ""}
        ${b.legInt.length ? `<label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.legitimateInterest || "Legitimate interest"} (${t.vendorsTitle || "Partners"}): ${v.name}`)}" data-vli="${v.id}" ${this.state.vendorLegitimateInterests[v.id] ? "checked" : ""}/> ${esc(t.legIntAllowed)}</label>` : ""}
      </div></div>`;
    }).join("");

    const customs = (this.config.customVendors || []).map((v) => `
      <div class="row"><div><h3>${esc(v.name)}</h3><p>${esc((v.purposes || []).join(", "))}</p>
      ${v.privacyPolicyUrl ? `<p><a href="${esc(v.privacyPolicyUrl)}" target="_blank" rel="noopener">${esc(t.privacyPolicy || "Privacy policy")}</a></p>` : ""}</div>
      <div class="toggles">
        <label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.consent || "Consent"}: ${v.name}`)}" data-cv="${v.key}" ${this.state.customVendors[v.key] ? "checked" : ""} ${v.legalBasis === "necessary" ? "disabled" : ""}/> ${esc(t.consent || "Consent")}</label>
      </div></div>`).join("");

    if (this.activeTab === "legint" && !hasLi) this.activeTab = "purposes";
    if (this.activeTab === "custom" && !customs) this.activeTab = "purposes";
    const tab = this.activeTab;
    const tabBtn = (key: string, label: string) =>
      `<button type="button" class="tab" role="tab" id="cmp-tab-${key}" data-tab="${key}" aria-controls="cmp-panel-${key}" aria-selected="${tab === key}" tabindex="${tab === key ? 0 : -1}">${esc(label)}</button>`;
    const panel = (key: string) =>
      `data-panel="${key}" role="tabpanel" id="cmp-panel-${key}" aria-labelledby="cmp-tab-${key}" tabindex="0" ${tab === key ? "" : "hidden"}`;
    const rejectLabel = this.hasPriorDecision ? t.withdrawAll : (t.rejectAll || "Reject all");

    return `<h2 id="cmp-title">${esc(t.customize || "Settings")}</h2>
      <div class="tabs" role="tablist" aria-label="${esc(t.customize || "Settings")}">
        ${tabBtn("purposes", t.purposesTitle || "Purposes")}
        ${hasLi ? tabBtn("legint", t.legitimateInterestTab) : ""}
        ${tabBtn("features", t.featuresTitle || "Features")}
        ${tabBtn("vendors", t.vendorsTitle || "Partners")}
        ${customs ? tabBtn("custom", t.customVendorsTitle || "Other services") : ""}
      </div>
      <div ${panel("purposes")}>
        <p class="notice">${esc(t.choicesNotice)}</p>
        ${purposes || "<p>–</p>"}
        ${specialPurposes ? `<h3 class="section-title">${esc(t.specialPurposesTitle || "Special purposes")}</h3><p class="notice">${esc(t.specialPurposesNotice)}</p>${specialPurposes}` : ""}
      </div>
      ${hasLi ? `<div ${panel("legint")}>
        <p class="notice">${esc(t.liTabIntro)}</p>
        ${liPurposes}
        <h3 class="section-title">${esc(t.vendorsTitle || "Partners")}</h3>
        ${liVendors}
      </div>` : ""}
      <div ${panel("features")}>
        ${specialFeatures ? `<h3>${esc(t.specialFeaturesTitle || "Special features")}</h3>${specialFeatures}` : ""}
        ${features ? `<h3 class="section-title">${esc(t.featuresTitle || "Features")}</h3>${featureStd ? `<div class="std">${esc(featureStd)}</div>` : ""}${features}` : ""}
      </div>
      <div ${panel("vendors")}>
        <p class="notice">${esc(fill(t.iabVendorsCount, { count: (gvl.vendors || []).length }))}</p>
        ${vendors || "<p>–</p>"}
      </div>
      ${customs ? `<div ${panel("custom")}><p class="notice">${esc(t.nonIabNotice)}</p>${customs}</div>` : ""}
      <p class="notice" style="margin-top:12px">${esc(fill(t.cmpStorage, { days: CMP_COOKIE_DAYS }))}</p>
      <div class="actions">
        <button type="button" class="primary" data-a="save">${esc(t.save || "Save")}</button>
        <button type="button" class="secondary" data-a="reject">${esc(rejectLabel)}</button>
        <button type="button" class="secondary" data-a="accept">${esc(t.acceptAll || "Accept all")}</button>
        <button type="button" class="ghost" data-a="back">${esc(t.back || "Back")}</button>
      </div>
      ${this.brandingFooter()}`;
  }

  private brandingEnabled() {
    const v = this.ui().showBranding;
    return v !== false && v !== "false";
  }

  private brandingFooter() {
    if (!this.brandingEnabled()) return "";
    return `<div class="branding"><a href="https://adconsent.de" target="_blank" rel="noopener noreferrer">powered by adconsent</a></div>`;
  }

  private purposeRow(t: Record<string, string>, p: GvlPurpose) {
    const d = this.disclosure;
    const vname = (id: number) => (this.config.gvl.vendors || []).find((v) => v.id === id)?.name || `#${id}`;
    const consentVendors = d.consentPurposeVendors.get(p.id) || [];
    const liVendors = d.liPurposeVendors.get(p.id) || [];
    // Only a consent toggle when at least one vendor actually asks consent for it.
    const toggle = consentVendors.length
      ? `<label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.consent || "Consent"}: ${p.name}`)}" data-pc="${p.id}" ${this.state.purposeConsents[p.id] ? "checked" : ""}/> ${esc(t.consent || "Consent")}</label>`
      : "";
    const liHint = liVendors.length
      ? `${vendorList(t, t.vendorCountLi, liVendors, vname)}<p class="notice"><a href="#" class="li-link" data-a="li-tab">${esc(t.liLink)}</a></p>`
      : "";
    return `<div class="row"><div><h3>${esc(p.name)}</h3><p>${esc(p.description || "")}</p>${illustrations(t, p)}
      ${consentVendors.length ? vendorList(t, t.vendorCountConsent, consentVendors, vname) : ""}
      ${liHint}</div>
      <div class="toggles">${toggle}</div></div>`;
  }

  private loadDisclosure(id: number) {
    if (!dsdBase || dsdPending.has(id) || typeof fetch !== "function") return;
    dsdPending.add(id);
    const ver = this.config.gvl.vendorListVersion || "latest";
    fetch(`${dsdBase}/c/dsd/${encodeURIComponent(String(ver))}/${id}.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { dsdCache.set(id, (j && j.disclosure) ?? null); })
      .catch(() => { dsdCache.set(id, null); })
      .finally(() => {
        dsdPending.delete(id);
        if (this.vendorDetailId === id && dsdCache.get(id)) { this.read(); this.render(); }
      });
  }

  private vendorDetail(t: Record<string, string>, id: number): string {
    const v = (this.config.gvl.vendors || []).find((x) => x.id === id);
    if (!v) { this.vendorDetailId = null; return this.second(t); }

    const purposeName = (pid: number) =>
      (this.config.gvl.purposes || []).find((p) => p.id === pid)?.name || String(pid);
    const specialPurposeName = (pid: number) =>
      (this.config.gvl.specialPurposes || []).find((p) => p.id === pid)?.name || String(pid);
    const featureName = (fid: number) =>
      (this.config.gvl.features || []).find((f) => f.id === fid)?.name || String(fid);
    const specialFeatureName = (fid: number) =>
      (this.config.gvl.specialFeatures || []).find((f) => f.id === fid)?.name || String(fid);

    const basis = this.disclosure.vendors.get(v.id);
    const consentNames = (basis?.consent || []).map(purposeName).join(", ") || "–";
    const liNames = (basis?.legInt || []).map(purposeName);
    const spNames = (v.specialPurposes || []).map(specialPurposeName).join(", ") || "–";
    const featNames = (v.features || []).map(featureName).join(", ") || "–";
    const sfNames = (v.specialFeatures || []).map(specialFeatureName).join(", ") || "–";
    const cats = (v.dataDeclaration || []).map((cid) => {
      const found = (this.config.gvl.dataCategories || []).find((c) => c.id === cid);
      return found?.name || String(cid);
    }).join(", ") || "–";

    const retention = formatRetention(v, t, purposeName, specialPurposeName);
    const storage = formatStorage(v, t) + (v.cookieRefresh ? ` — ${t.cookieRefreshNote}` : "");
    const dsd = v.deviceStorageDisclosure ?? dsdCache.get(v.id);
    if (dsd === undefined && v.deviceStorageDisclosureUrl) this.loadDisclosure(v.id);
    const disclosure = renderDisclosureTable(dsd, t, purposeName);

    const liRow = liNames.length
      ? `<dt>${esc(t.legitimateInterest || "LI")}</dt><dd>${esc(liNames.join(", "))}</dd>`
      : "";

    return `<h2 id="cmp-title">${esc(t.vendorDetail || "Partner")} — ${esc(v.name)}</h2>
      <div class="detail">
        <dl>
          <dt>${esc(t.consent || "Consent")}</dt><dd>${esc(consentNames)}</dd>
          ${liRow}
          <dt>${esc(t.specialPurposesTitle || "Special purposes")}</dt><dd>${esc(spNames)}</dd>
          <dt>${esc(t.featuresTitle || "Features")}</dt><dd>${esc(featNames)}</dd>
          <dt>${esc(t.specialFeaturesTitle || "Special features")}</dt><dd>${esc(sfNames)}</dd>
          <dt>${esc(t.dataCategoriesTitle || "Data categories")}</dt><dd>${esc(cats)}</dd>
          <dt>${esc(t.retentionTitle || "Retention")}</dt><dd>${retention}</dd>
          <dt>${esc(t.deviceStorageTitle || "Device storage")}</dt><dd>${esc(storage)}</dd>
        </dl>
        ${v.policyUrl ? `<p><a href="${esc(v.policyUrl)}" target="_blank" rel="noopener">${esc(t.privacyPolicy || "Privacy policy")}</a></p>` : ""}
        ${v.legIntClaimUrl && liNames.length ? `<p><a href="${esc(v.legIntClaimUrl)}" target="_blank" rel="noopener">${esc(t.legIntClaim || "Legitimate interest information")}</a></p>` : ""}
        ${v.deviceStorageDisclosureUrl ? `<p><a href="${esc(v.deviceStorageDisclosureUrl)}" target="_blank" rel="noopener">${esc(t.deviceStorageDisclosureLink || "Device storage disclosure (JSON)")}</a></p>` : ""}
        ${disclosure}
      </div>
      <div class="toggles" style="margin:12px 0">
        ${(basis?.consent || []).length
          ? `<label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.consent || "Consent"}: ${v.name}`)}" data-vc="${v.id}" ${this.state.vendorConsents[v.id] ? "checked" : ""}/> ${esc(t.consent || "Consent")}</label>`
          : ""}
        ${(basis?.legInt || []).length
          ? `<label class="toggle"><input type="checkbox" role="switch" aria-label="${esc(`${t.legitimateInterest || "Legitimate interest"}: ${v.name}`)}" data-vli="${v.id}" ${this.state.vendorLegitimateInterests[v.id] ? "checked" : ""}/> ${esc(t.legIntAllowed)}</label>`
          : ""}
      </div>
      <div class="actions">
        <button type="button" class="secondary" data-a="vendor-back">${esc(t.back || "Back")}</button>
        <button type="button" class="primary" data-a="save">${esc(t.save || "Save")}</button>
      </div>
      ${this.brandingFooter()}`;
  }

  private bind() {
    const r = this.shadow;
    r.querySelector('[data-a="accept"]')?.addEventListener("click", () => this.callbacks.onAcceptAll());
    r.querySelector('[data-a="reject"]')?.addEventListener("click", () => this.callbacks.onRejectAll());
    r.querySelector('[data-a="settings"]')?.addEventListener("click", () => this.callbacks.onOpenSettings());
    r.querySelector('[data-a="back"]')?.addEventListener("click", () => this.callbacks.onCloseSettings());
    r.querySelector('[data-a="dns"]')?.addEventListener("click", () => this.callbacks.onDoNotSell?.());
    r.querySelector('[data-a="save"]')?.addEventListener("click", () => { this.read(); this.callbacks.onSave(this.state); });
    r.querySelector('[data-a="li-link"]')?.addEventListener("click", (e) => {
      e.preventDefault();
      this.activeTab = "legint";
      this.callbacks.onOpenSettings();
    });
    r.querySelectorAll('[data-a="li-tab"]').forEach((a) => a.addEventListener("click", (e) => {
      e.preventDefault();
      this.read();
      this.activeTab = "legint";
      this.render();
    }));
    r.querySelector('[data-a="vendors-link"]')?.addEventListener("click", (e) => {
      e.preventDefault();
      this.activeTab = "vendors";
      this.callbacks.onOpenSettings();
    });
    r.querySelector('[data-a="disclosure-toggle"]')?.addEventListener("click", () => {
      const body = r.querySelector("[data-disclosure-body]") as HTMLElement | null;
      const btn = r.querySelector('[data-a="disclosure-toggle"]') as HTMLButtonElement | null;
      if (!body || !btn) return;
      const open = body.hasAttribute("hidden");
      if (open) body.removeAttribute("hidden"); else body.setAttribute("hidden", "");
      btn.textContent = open ? (this.t().showLess || "Show less") : (this.t().showMore || "Show more");
    });
    r.querySelector('[data-a="vendor-back"]')?.addEventListener("click", () => {
      this.read();
      this.vendorDetailId = null;
      this.activeTab = "vendors";
      this.render();
    });
    const tabs = Array.from(r.querySelectorAll<HTMLElement>("[role=tab][data-tab]"));
    const activate = (key: string, focus: boolean) => {
      this.read();
      this.activeTab = key;
      if (focus) this.focusTab = key;
      this.render();
    };
    tabs.forEach((btn, i) => {
      btn.addEventListener("click", () => activate(btn.dataset.tab!, true));
      // WAI-ARIA tabs pattern: arrows / Home / End move focus and activate (roving tabindex).
      btn.addEventListener("keydown", (e: KeyboardEvent) => {
        let next = -1;
        if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
        else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = tabs.length - 1;
        if (next < 0) return;
        e.preventDefault();
        activate(tabs[next].dataset.tab!, true);
      });
    });
    // The same vendor LI switch appears in the LI tab and in the partner list: keep
    // all copies in sync so read() never sees conflicting values.
    r.querySelectorAll<HTMLInputElement>("[data-vli]").forEach((el) => el.addEventListener("change", () => {
      r.querySelectorAll<HTMLInputElement>(`[data-vli="${el.dataset.vli}"]`).forEach((o) => { o.checked = el.checked; });
      this.state.vendorLegitimateInterests[+el.dataset.vli!] = el.checked;
    }));
    r.querySelectorAll("[data-vd]").forEach((a) => a.addEventListener("click", (e) => {
      e.preventDefault();
      this.read();
      this.vendorDetailId = +(a as HTMLElement).dataset.vd!;
      this.render();
    }));
  }

  private read() {
    this.shadow.querySelectorAll<HTMLInputElement>("[data-pc]").forEach((el) => { this.state.purposeConsents[+el.dataset.pc!] = el.checked; });
    this.shadow.querySelectorAll<HTMLInputElement>("[data-pli]").forEach((el) => {
      const id = +el.dataset.pli!;
      this.state.purposeLegitimateInterests[id] = NO_LI_PURPOSE_IDS.has(id) ? false : el.checked;
    });
    this.shadow.querySelectorAll<HTMLInputElement>("[data-vc]").forEach((el) => { this.state.vendorConsents[+el.dataset.vc!] = el.checked; });
    const seenVli = new Set<string>();
    this.shadow.querySelectorAll<HTMLInputElement>("[data-vli]").forEach((el) => {
      if (seenVli.has(el.dataset.vli!)) return;
      seenVli.add(el.dataset.vli!);
      this.state.vendorLegitimateInterests[+el.dataset.vli!] = el.checked;
    });
    this.shadow.querySelectorAll<HTMLInputElement>("[data-sf]").forEach((el) => { this.state.specialFeatureOptins[+el.dataset.sf!] = el.checked; });
    this.shadow.querySelectorAll<HTMLInputElement>("[data-cv]").forEach((el) => { this.state.customVendors[el.dataset.cv!] = el.checked; });
  }
}

function illustrations(t: Record<string, string>, item: GvlPurpose) {
  const list = item.illustrations || [];
  if (!list.length) return "";
  return `<div class="illus"><strong>${esc(t.illustrations || "Examples")}:</strong><ul>${list.map((i) => `<li>${esc(i)}</li>`).join("")}</ul></div>`;
}

function formatDuration(seconds: number | string | null | undefined, t: Record<string, string>): string {
  if (seconds == null || seconds === "") return "–";
  const n = Number(seconds);
  if (!Number.isFinite(n)) return String(seconds);
  if (n < 0) return t.session || "session";
  if (n === 0) return t.session || "session";
  if (n % 86400 === 0) return `${n / 86400} ${t.days || "days"}`;
  if (n % 3600 === 0) return `${n / 3600} ${t.hours || "hours"}`;
  return `${n} ${t.seconds || "seconds"}`;
}

function formatRetention(
  v: GvlVendor, t: Record<string, string>,
  purposeName: (id: number) => string, specialPurposeName: (id: number) => string
) {
  const dr = v.dataRetention;
  if (!dr) return "–";
  const parts: string[] = [];
  if (dr.stdRetention != null) {
    parts.push(`${esc(t.stdRetention || "Standard retention")}: ${dr.stdRetention} ${esc(t.days || "days")}`);
  }
  if (dr.purposes) {
    for (const [pid, days] of Object.entries(dr.purposes)) {
      parts.push(`${esc(purposeName(+pid))}: ${days} ${esc(t.days || "days")}`);
    }
  }
  if (dr.specialPurposes) {
    for (const [pid, days] of Object.entries(dr.specialPurposes)) {
      parts.push(`${esc(specialPurposeName(+pid))}: ${days} ${esc(t.days || "days")}`);
    }
  }
  return parts.join("<br>") || "–";
}

function formatStorage(v: GvlVendor, t: Record<string, string>) {
  const yn = (b?: boolean) => (b ? (t.yes || "yes") : (t.no || "no"));
  const parts = [
    `${t.usesCookies || "Cookies"}: ${yn(v.usesCookies)}`,
    v.cookieMaxAgeSeconds != null && v.cookieMaxAgeSeconds !== ""
      ? `${t.cookieMaxAge || "Max age"}: ${formatDuration(v.cookieMaxAgeSeconds, t)}`
      : "",
    `${t.cookieRefresh || "Refresh"}: ${yn(v.cookieRefresh)}`,
    `${t.nonCookieAccess || "Non-cookie"}: ${yn(v.usesNonCookieAccess)}`
  ].filter(Boolean);
  return parts.join(" · ");
}

function renderDisclosureTable(dsd: unknown, t: Record<string, string>, purposeName: (id: number) => string) {
  const raw = dsd as { disclosures?: Array<Record<string, unknown>> } | null;
  const rows = Array.isArray(raw?.disclosures) ? raw!.disclosures! : [];
  if (!rows.length) return "";

  const body = rows.map((row) => {
    const name = String(row.identifier ?? row.name ?? "–");
    const typ = String(row.type ?? "–");
    const dur = formatDuration(row.maxAgeSeconds as number | undefined, t);
    const domains = Array.isArray(row.domains) ? (row.domains as string[]).join(", ") : String(row.domain ?? "–");
    const purposes = Array.isArray(row.purposes)
      ? (row.purposes as number[]).map(purposeName).join(", ")
      : "–";
    return `<tr><td>${esc(name)}</td><td>${esc(typ)}</td><td>${esc(dur)}</td><td>${esc(domains)}</td><td>${esc(purposes)}</td></tr>`;
  }).join("");

  const collapsed = rows.length > 3;
  return `<div style="margin-top:10px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px">
        <strong style="font-size:.8rem">${esc(t.disclosureTitle || "Device storage details")}</strong>
        ${collapsed ? `<button type="button" class="ghost" data-a="disclosure-toggle" style="font-size:.75rem">${esc(t.showMore || "Show more")}</button>` : ""}
      </div>
      <div data-disclosure-body ${collapsed ? "hidden" : ""}>
        <table style="width:100%;border-collapse:collapse;font-size:.7rem;margin-top:6px">
          <thead><tr>
            <th style="text-align:left;border-bottom:1px solid #e2e8f0;padding:4px">${esc(t.disclosureName || "Name")}</th>
            <th style="text-align:left;border-bottom:1px solid #e2e8f0;padding:4px">${esc(t.disclosureType || "Type")}</th>
            <th style="text-align:left;border-bottom:1px solid #e2e8f0;padding:4px">${esc(t.disclosureDuration || "Duration")}</th>
            <th style="text-align:left;border-bottom:1px solid #e2e8f0;padding:4px">${esc(t.disclosureDomain || "Domain")}</th>
            <th style="text-align:left;border-bottom:1px solid #e2e8f0;padding:4px">${esc(t.disclosurePurposes || "Purposes")}</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </div>`;
}

function fill(template: string | undefined, vars: Record<string, string | number>) {
  let out = String(template || "");
  for (const [k, v] of Object.entries(vars)) out = out.split(`%{${k}}`).join(String(v));
  return out;
}

/** "N partners …" with an expandable list of their names (Appendix B C(c)(II)). */
function vendorList(t: Record<string, string>, label: string, ids: number[], name: (id: number) => string) {
  if (!ids.length) return "";
  return `<details class="vlist"><summary>${esc(fill(label, { count: ids.length }))} — ${esc(t.showVendors || "Show partners")}</summary>
    <ul>${ids.map((id) => `<li>${esc(name(id))}</li>`).join("")}</ul></details>`;
}

function esc(v: string) {
  return String(v ?? "").split("&").join("&amp;").split("<").join("&lt;").split(">").join("&gt;").split('"').join("&quot;");
}
